import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import cx from 'classnames';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { IconAddUser } from '../../../../../design-system/components/icons/AddUser';
import { IconChevronDown } from '../../../../../design-system/components/icons/ChevronDown';
import { IconHeartFilled } from '../../../../../design-system/components/icons/HeartFilled';
import { IconLocalActivity } from '../../../../../design-system/components/icons/LocalActivity';
import { IconLuggage } from '../../../../../design-system/components/icons/Luggage';
import { IconSettings } from '../../../../../design-system/components/icons/Settings';
import { IconSignOut } from '../../../../../design-system/components/icons/SignOut';
import { IconStars } from '../../../../../design-system/components/icons/Stars';
import { MenuItem } from '../../../../../design-system/components/nav/menu-item/MenuItem';
import { NavBarItem } from '../../../../../design-system/components/nav/nav-bar-item/NavBarItem';
import { Typography } from '../../../../../design-system/components/typography/Typography';
import { User } from '../../../../../resources/user/domain/types/User';
import { useShowLoyaltyProgram } from '../../../../../shared/hooks/useShowLoyaltyProgram';
import { useShowReferAfriend } from '../../../../../shared/hooks/useShowReferAfriend';
import { useUrlGenerator } from '../../../../../shared/hooks/useUrlGenerator';
import { useFeatureDiscoveryNotification } from '../../../../../shared/providers/user/useFeatureDiscoveryNotification';
import { PickDomain } from '../../../../../shared/utilities/types/PickDomain';

import style from './UserAccountDropdown.module.css';

interface Props {
    user: PickDomain<
        User,
        'firstName' | 'recommendationsIsEnabled',
        {
            status: User['status'] | null;
        }
    >;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    className?: string;
}

export function UserAccountDropdown({
    user,
    isOpen,
    onOpenChange,
    onMouseEnter,
    onMouseLeave,
    className,
}: Props) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();
    const showLoyaltyProgram = useShowLoyaltyProgram();
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const showReferAFriend = useShowReferAfriend();
    const { hasNotifications, canShowMyBenefitsNotification, canShowReferAfriendNotification } =
        useFeatureDiscoveryNotification();

    return (
        <DropdownMenu.Root modal={false} open={isOpen}>
            <DropdownMenu.Trigger
                asChild
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={() => {
                    onOpenChange(!isOpen);

                    trackEvent((ampli, defaultProperties) => {
                        ampli.primaryNavAccountClicked({
                            ...defaultProperties,
                            block: 'primary_nav',
                        });
                    });
                }}
            >
                <NavBarItem
                    as="button"
                    ref={triggerRef}
                    data-testid="PrimaryNav-UserAccount-Dropdown-Trigger-Button"
                    className={cx('amp-mask', className)}
                    displayDesktopIconLeading={true}
                    iconLeading={
                        <ProfileLetter
                            hasFeatureDiscoveryNotification={hasNotifications}
                            name={user.firstName || 'Evaneos'}
                        />
                    }
                    iconTrailing={<IconChevronDown />}
                    isMenuOpen={isOpen}
                >
                    {user.firstName ||
                        formatMessage({
                            id: 'global.header.globalNav.account.label',
                        })}
                </NavBarItem>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    side="bottom"
                    align="end"
                    sideOffset={18}
                    className={style.menu}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onPointerDownOutside={(event) => {
                        // Avoid closing the dropdown when clicking on the dropdown trigger button.
                        let { target } = event;
                        while (target && target !== document.body) {
                            if (!(target instanceof HTMLElement)) {
                                return;
                            }
                            if (target.id === triggerRef.current?.id) {
                                return;
                            }
                            target = target.parentElement;
                        }

                        // Because this menu is not like the others on small screens,
                        // it is not full width & full height so user can click outside to close.
                        onOpenChange(false);
                    }}
                >
                    <DropdownMenu.Item asChild>
                        <MenuItem
                            className={style.menuListItem}
                            href={generatePath('account_trips')}
                            Icon={IconLuggage}
                            onClick={() => {
                                trackEvent((ampli, defaultProperties) => {
                                    ampli.primaryNavTripsListClicked({
                                        ...defaultProperties,
                                        block: 'primary_nav',
                                    });
                                });
                            }}
                        >
                            {formatMessage({
                                id: 'global.header.globalNav.account.trips',
                            })}
                        </MenuItem>
                    </DropdownMenu.Item>
                    {user.recommendationsIsEnabled ? (
                        <DropdownMenu.Item asChild>
                            <MenuItem
                                className={style.menuListItem}
                                href={generatePath('account_recommendations', {
                                    source: 'navigation_bar',
                                })}
                                Icon={IconStars}
                                onClick={() => {
                                    trackEvent((ampli, defaultProperties) => {
                                        ampli.primaryNavRecommendationClicked({
                                            ...defaultProperties,
                                            block: 'primary_nav',
                                        });
                                    });
                                }}
                            >
                                {formatMessage({
                                    id: 'global.header.globalNav.recommendations.label',
                                })}
                            </MenuItem>
                        </DropdownMenu.Item>
                    ) : null}
                    {showLoyaltyProgram ? (
                        <DropdownMenu.Item asChild>
                            <MenuItem
                                className={style.menuListItem}
                                href={generatePath('account_my_benefits')}
                                Icon={IconLocalActivity}
                                onClick={() => {
                                    trackEvent((ampli, defaultProperties) => {
                                        ampli.primaryNavBenefitsClicked({
                                            ...defaultProperties,
                                        });
                                    });
                                }}
                                hasFeatureDiscoveryNotification={canShowMyBenefitsNotification}
                            >
                                {formatMessage({
                                    id: 'global.header.globalNav.myBenefits.label',
                                })}
                            </MenuItem>
                        </DropdownMenu.Item>
                    ) : null}
                    <DropdownMenu.Item asChild>
                        <MenuItem
                            className={style.menuListItem}
                            href={generatePath('account_wishlist')}
                            Icon={IconHeartFilled}
                            onClick={() => {
                                trackEvent((ampli, defaultProperties) => {
                                    ampli.primaryNavFavoriteClicked({
                                        ...defaultProperties,
                                        block: 'primary_nav',
                                    });
                                });
                            }}
                        >
                            {formatMessage({
                                id: 'global.header.globalNav.account.wishes',
                            })}
                        </MenuItem>
                    </DropdownMenu.Item>
                    {showReferAFriend ? (
                        <DropdownMenu.Item asChild>
                            <MenuItem
                                className={style.menuListItem}
                                href={generatePath('account_refer_a_friend')}
                                Icon={IconAddUser}
                                onClick={() => {
                                    trackEvent((ampli, defaultProperties) => {
                                        ampli.primaryNavReferAFriendClicked({
                                            ...defaultProperties,
                                            block: 'primary_nav',
                                        });
                                    });
                                }}
                                hasFeatureDiscoveryNotification={canShowReferAfriendNotification}
                            >
                                {formatMessage({
                                    id: 'global.header.globalNav.account.referAFriend',
                                })}
                            </MenuItem>
                        </DropdownMenu.Item>
                    ) : null}
                    <DropdownMenu.Item asChild>
                        <MenuItem
                            className={style.menuListItem}
                            href={generatePath('account_settings')}
                            Icon={IconSettings}
                            onClick={() => {
                                trackEvent((ampli, defaultProperties) => {
                                    ampli.primaryNavPersonalInfoClicked({
                                        ...defaultProperties,
                                        block: 'primary_nav',
                                    });
                                });
                            }}
                        >
                            {formatMessage({
                                id: 'global.header.globalNav.account.settings',
                            })}
                        </MenuItem>
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator asChild>
                        <hr className={style.hr} />
                    </DropdownMenu.Separator>
                    <DropdownMenu.Item asChild>
                        <MenuItem
                            className={style.menuListItem}
                            href={generatePath('logout')}
                            Icon={IconSignOut}
                            onClick={() => {
                                trackEvent((ampli, defaultProperties) => {
                                    ampli.primaryNavLogoutClicked({
                                        ...defaultProperties,
                                        block: 'primary_nav',
                                    });
                                });
                            }}
                        >
                            {formatMessage({
                                id: 'global.header.globalNav.account.logout',
                            })}
                        </MenuItem>
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}

function ProfileLetter({
    name,
    hasFeatureDiscoveryNotification,
}: {
    name: string;
    hasFeatureDiscoveryNotification: boolean;
}) {
    return (
        <Typography
            data-testid="Badge"
            className={cx(style.profileLetter, {
                [style.hasFeatureDiscoveryNotification]: hasFeatureDiscoveryNotification,
            })}
            scale="primary-xxs-bold"
        >
            {name[0]}
        </Typography>
    );
}
